// Paso 2 del onboarding externo de Tiendanube: conectar la tienda, traer los productos
// e importarlos a Atlas. Reusa la capa que ya existe (`abrirVentanaOAuth` + `ensureTnProducts`
// + `fireTnImport`): el usuario ya tiene empresa (la creó /tiendanube al registrarse), así que
// `tn-connect` funciona tal cual. `brandId=null` → la marca default para leer; para importar
// resolvemos el brand_id default real (n8n lo usa para asignar los productos a la marca).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  abrirVentanaOAuth,
  ensureTnProducts,
  fireTnImport,
  getTnStatus,
  type TnProduct,
} from '../../lib/tiendaNube';
import { createCompanyForUser } from '../../lib/createCompany';
import { MARCA_POR_DEFECTO } from '../../lib/altaDesdeCuestionario';
import { paisDelBrowser } from '../../lib/paisDelBrowser';
import { Feedback } from './authPrimitives';

// Import en tandas, igual criterio que AgregarVariosModal (el back procesa por worker/queue).
const TN_BATCH = 25;

export default function TiendanubeConectar() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [importing, setImporting] = useState(false);
  const [storeName, setStoreName] = useState<string | null>(null);
  const [products, setProducts] = useState<TnProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Install-first: la marca donde tn-link-claim vinculó la tienda (para traer/importar los productos
  // ahí, no en la default).
  const [claimBrandId, setClaimBrandId] = useState<string | null>(null);

  // Sin sesión → volver a la pantalla de registro/login (no se llega acá sin autenticar).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!data.user) {
        navigate('/tiendanube', { replace: true });
        return;
      }
      // Salvavidas: quien llegó por Google/login SIN empresa la necesita antes de importar (el
      // registro por mail ya la crea, y "Continuar" también). Rubro placeholder 'Otros' — la IA lo
      // ajusta al conectar la tienda. `createCompanyForUser` es idempotente.
      const { data: prof } = await supabase
        .from('profiles')
        .select('id_company, name')
        .eq('id', data.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (!(prof as { id_company?: string | null } | null)?.id_company) {
        const meta = data.user.user_metadata as { full_name?: string } | undefined;
        try {
          await createCompanyForUser({
            userId: data.user.id,
            name:
              (prof as { name?: string } | null)?.name ||
              meta?.full_name ||
              data.user.email?.split('@')[0] ||
              'Mi empresa',
            pais: paisDelBrowser() ?? 'Otro',
            companyName: MARCA_POR_DEFECTO,
            industry: 'Otros',
          });
        } catch {
          /* si falla la creación, no trabamos la pantalla: el import avisará si falta empresa */
        }
        if (cancelled) return;
      }

      // Install-first: si venimos con un claim (tienda ya autorizada, en espera), la vinculamos a ESTA
      // cuenta con tn-link-claim (sin re-hacer OAuth) y traemos sus productos. Ver edge tn-link-claim.
      let claimPend: string | null = null;
      try {
        claimPend = localStorage.getItem('tn-install-claim');
      } catch {
        /* storage no disponible */
      }
      if (claimPend) {
        try {
          const { data: linkData, error: linkErr } = await supabase.functions.invoke('tn-link-claim', {
            body: { claim: claimPend },
          });
          if (cancelled) return;
          if (linkErr) {
            let code: string | undefined;
            let serverMsg: string | undefined;
            try {
              const b = await (linkErr as { context?: Response }).context?.json();
              code = b?.code;
              serverMsg = b?.error;
            } catch {
              /* sin cuerpo legible */
            }
            // El claim se descarta SÓLO cuando reintentar no cambia nada: éxito (más abajo) o error
            // TERMINAL. En errores transitorios (500 → tn-link-claim libera `claimed_at`) NO lo borramos,
            // así una recarga reintenta sin obligar al comerciante a re-autorizar la tienda de cero.
            // `sin_empresa` NO es terminal: tn-link-claim lo devuelve ANTES de consumir el claim (si el
            // salvavidas que crea la empresa falló transitorio), así que una recarga puede reintentar.
            const terminal =
              code === 'tienda_en_otra_cuenta' ||
              code === 'claim_invalido' ||
              code === 'claim_vencido';
            if (terminal) {
              try {
                localStorage.removeItem('tn-install-claim');
              } catch {
                /* noop */
              }
              setError(serverMsg || 'No se pudo conectar tu tienda. Probá de nuevo.');
              setChecking(false);
              return;
            }
            // Transitorio: dejamos el claim y avisamos para que una recarga reintente.
            setError(serverMsg || 'No pudimos conectar tu tienda. Recargá la página para reintentar.');
            setChecking(false);
            return;
          }
          // Éxito: la tienda quedó vinculada → el claim ya cumplió, lo borramos.
          try {
            localStorage.removeItem('tn-install-claim');
          } catch {
            /* noop */
          }
          const bId = (linkData as { brand_id?: string | null } | null)?.brand_id ?? null;
          setClaimBrandId(bId);
          try {
            // tn-products puede devolver 0 por timing justo tras vincular la tienda (la consulta a
            // Tiendanube pega demasiado rápido). Reintentamos un par de veces con pausa antes de dar
            // el catálogo por vacío, para no meter al usuario nuevo sin sus productos.
            let res = await ensureTnProducts(bId);
            for (let intento = 0; res.products.length === 0 && intento < 2; intento++) {
              await new Promise((r) => setTimeout(r, 1500));
              if (cancelled) return;
              res = await ensureTnProducts(bId);
            }
            if (cancelled) return;
            setStoreName(res.storeName);
            setProducts(res.products);
            setConnected(true);
          } catch (e) {
            if (cancelled) return;
            setError(e instanceof Error ? e.message : 'No se pudieron traer los productos.');
          }
          setChecking(false);
          return;
        } catch {
          if (cancelled) return;
          // Excepción de red (transitoria): NO borramos el claim; que una recarga reintente.
          setError('No pudimos conectar tu tienda. Recargá la página para reintentar.');
          setChecking(false);
          return;
        }
      }

      // Si el usuario YA tiene su tienda conectada, no se la pedimos de nuevo:
      // vamos directo al estudio con sus productos.
      try {
        const status = await getTnStatus(null);
        if (cancelled) return;
        if (status.connected) {
          navigate('/', { replace: true });
          return;
        }
      } catch {
        /* si el chequeo falla, mostramos el botón de conectar igual */
      }
      if (!cancelled) setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleConnect = async () => {
    setError(null);
    // El popup se abre EN el click, sincrónico, antes de cualquier await (Safari/iOS).
    // Ver ADR-2026-08-07-tiendanube-popup-safari.
    const popup = abrirVentanaOAuth();
    setConnecting(true);
    try {
      const { products: prods, storeName: name } = await ensureTnProducts(null, { popup });
      setStoreName(name);
      setProducts(prods);
      setConnected(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg !== 'CANCELLED') setError(msg || 'No se pudo conectar tu tienda. Probá de nuevo.');
    } finally {
      setConnecting(false);
    }
  };

  const handleEmpezar = async () => {
    setError(null);
    setImporting(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      // Empresa + marca default del usuario (para asignar los productos importados).
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user?.id ?? '')
        .maybeSingle();
      const companyId = (profile as { id_company?: string | null } | null)?.id_company ?? null;
      // Install-first: importamos a la marca donde quedó la tienda (claimBrandId); si no, a la default.
      let brandId = claimBrandId;
      if (!brandId) {
        const { data: brand } = await supabase
          .from('brand_profiles')
          .select('id')
          .eq('id_company', companyId ?? '')
          .eq('is_default', true)
          .maybeSingle();
        brandId = (brand as { id?: string } | null)?.id ?? null;
      }

      // Mismo mapeo TnProduct → payload que AgregarVariosModal, en tandas de 25.
      const payload = products.map((t) => ({
        nombre: t.nombre,
        imgs: t.imgs,
        tn_product_id: t.id,
        variantes: t.variantes,
      }));
      for (let i = 0; i < payload.length; i += TN_BATCH) {
        await fireTnImport({
          userId: user?.id,
          companyId,
          brandId,
          productos: payload.slice(i, i + TN_BATCH),
        });
      }
      // Damos unos segundos a que n8n arranque a crear los productos (mismo criterio que el modal),
      // y entramos a la app: los productos van apareciendo a medida que el import los procesa.
      await new Promise((r) => setTimeout(r, 3500));
      navigate('/tiendanube/producto', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron traer los productos. Probá de nuevo.');
      setImporting(false);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-[100dvh] w-full items-center justify-center bg-white">
        <Loader2 className="size-6 animate-spin text-[#71717B]" />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] w-full bg-white">
      <div className="mx-auto flex w-full max-w-[440px] animate-fade-in flex-col items-center gap-6 px-5 pb-10 pt-16 text-center">
        <img
          src="/images/estudio-atlas-logo-solo.svg"
          alt="Estudio Atlas"
          draggable={false}
          className="h-12 w-auto select-none"
        />

        {!connected ? (
          <>
            <div className="flex flex-col gap-2">
              <h1 className="text-[24px] font-[600] leading-[30px] text-[#18181B]">
                Conectá tu Tiendanube
              </h1>
              <p className="text-[14px] font-[500] leading-[20px] text-[#52525C]">
                Traemos tus productos para que generes imágenes profesionales con ellos.
              </p>
            </div>

            <Feedback error={error} message={null} />

            <button
              type="button"
              onClick={handleConnect}
              disabled={connecting}
              className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#18181B] px-4 py-4 text-[15px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A] active:bg-[#09090B] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {connecting && <Loader2 className="size-4 animate-spin" />}
              {connecting ? 'Conectando tu tienda…' : 'Conectar mi Tiendanube'}
            </button>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <h1 className="text-[24px] font-[600] leading-[30px] text-[#18181B]">
                ¡Tienda conectada! 🎉
              </h1>
              <p className="text-[14px] font-[500] leading-[20px] text-[#52525C]">
                Conectamos{' '}
                <span className="font-[700] text-[#18181B]">{storeName || 'tu tienda'}</span> y
                encontramos <span className="font-[700] text-[#18181B]">{products.length}</span>{' '}
                {products.length === 1 ? 'producto' : 'productos'}.
              </p>
            </div>

            <Feedback error={error} message={null} />

            <button
              type="button"
              onClick={handleEmpezar}
              disabled={importing}
              className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#18181B] px-4 py-4 text-[15px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A] active:bg-[#09090B] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {importing && <Loader2 className="size-4 animate-spin" />}
              {importing ? 'Trayendo tus productos…' : 'Empezar a generar'}
            </button>
            {importing && (
              <p className="text-[13px] font-[500] leading-[18px] text-[#71717B]">
                Estamos trayendo tus productos a Atlas. Van a aparecer en un ratito.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
